import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { ModelInfo, FeedVideoPayload } from '@/services/types';
import assetService from '@/services/amai/asset.service';
import { IMAGE_TO_VIDEO_EXAMPLES } from '@/features/generation/video/constants';
import { SLICE_MODEL_CONFIG, VIDEO_MODEL_IDS } from '@/features/generation/video/constants/config';
import { supportsMultiModalReference } from '@/features/generation/model-core';
import { reconstructPromptFromMetaList } from '@/utils/genImmediate';
import {
  VideoFormState,
  FrameImage,
  UploadedImage,
  VideoUIState,
} from '@/features/generation/video/types';

interface ModelMultimodalData {
  uploadedImages: UploadedImage[];
  videoReferences: FrameImage[];
  audioReference: FrameImage | null;
  audioReferences?: FrameImage[];
  docReferences?: FrameImage[]; // 多模态参考模式的文档引用（Wan3.0）
  referenceMode: string;
  firstFrame?: FrameImage | null;
  lastFrame?: FrameImage | null;
}

interface VideoGenerationState extends VideoFormState, VideoUIState {
  models: ModelInfo[];
  isLoading: boolean;
  error: string | null;
  imageHistory: {
    referenceImages: UploadedImage[];
    keyframes: {
      firstFrame: FrameImage | null;
      lastFrame: FrameImage | null;
    };
  };
  modelMultimodalData: Record<string, ModelMultimodalData>;
  isRestoringFromEdit: boolean; // 编辑回显标记：防止 selectedModels 变化时错误地清空 uploadedImages
}

const initialState: VideoGenerationState = {
  // Form state
  prompt: '',
  modelPrompts: {} as Record<string, string>,
  selectedModels: [],
  duration: '5 s',
  aspectRatioVideo: '16:9',
  resolutionVideo: '720p',
  resolution: '自动匹配',
  generationCount: 1,
  sound: 'off', // 同时生成声音：on | off
  real_video: false, // 真人视频开关（仅 Seedance 2.0）
  referenceTab: '参考图',
  uploadedImages: [],
  firstFrame: null,
  lastFrame: null,
  videoReference: null, // 多模态参考模式的视频引用（单个，向后兼容）
  videoReferences: [] as FrameImage[], // 多模态参考模式的多个视频引用
  audioReference: null, // 音频引用（单个，向后兼容）
  audioReferences: [] as FrameImage[], // 多模态参考模式的多个音频引用
  docReferences: [] as FrameImage[], // 多模态参考模式的文档引用（Wan3.0）
  referenceMode: '多模态参考', // 可灵3 Omni参考模式：首尾帧 | 多模态参考
  recommendImages: IMAGE_TO_VIDEO_EXAMPLES.slice(0, 5).map((example, index) => ({
    id: `recommend_${index}`,
    src: example.coverPageURL,
  })),

  // UI state
  swapHover: false,
  preview: {
    visible: false,
    frame: null,
  },

  // Data state
  models: [],
  isLoading: false,
  error: null,

  imageHistory: {
    referenceImages: [],
    keyframes: {
      firstFrame: null,
      lastFrame: null,
    },
  },
  modelMultimodalData: {},
  isRestoringFromEdit: false, // 编辑回显标记：防止 selectedModels 变化时错误地清空 uploadedImages
};

// 异步thunk：应用视频参数并获取资产信息
export const applyVideoPayloadWithAssets = createAsyncThunk(
  'videoGeneration/applyVideoPayloadWithAssets',
  async (payload: FeedVideoPayload, { rejectWithValue }) => {
    console.log(payload, 'payload')
    try {
      // 防御性检查：确保payload存在
      if (!payload) {
        return rejectWithValue('视频payload为空');
      }


      const result = {
        payload,
        firstFrameAsset: null as { id: string; url: string } | null,
        lastFrameAsset: null as { id: string; url: string } | null,
        referenceAssets: [] as { id: string; url: string }[],
        videoReferenceAsset: null as { id: string; url: string } | null, // 多模态参考模式的视频引用（单个，向后兼容）
        videoReferenceAssets: [] as { id: string; url: string }[], // 多模态参考模式的多个视频引用
        audioReferenceAssets: [] as { id: string; url: string }[], // 多模态参考模式的多个音频引用
        docReferenceAssets: [] as { id: string; url: string; name?: string }[], // 多模态参考模式的文档引用（Wan3.0）
      };

      const refImageIds = payload.ref_images || [];
      const tailImageId = payload.tail_image;
      const refVideoIds = payload.ref_videos || [];
      const refVideoUrls = payload.ref_videos_url || [];

      console.log('[applyVideoPayloadWithAssets] 检查多模态参考模式 payload 完整结构:', {
        mode_type: payload.mode_type,
        ref_images: payload.ref_images,
        tail_image: payload.tail_image,
        ref_videos: payload.ref_videos,
        ref_videos_url: payload.ref_videos_url,
        refImageIds,
        tailImageId,
        refVideoIds,
        refVideoUrls,
      });

      // 更精确地区分模式
      if (payload.mode_type === 3) {
        // 多模态参考模式：处理视频引用和参考图
        console.log('[applyVideoPayloadWithAssets] 进入多模态参考模式处理');
        
        // 支持多个视频引用（Seedance 2.0 多视频模式）
        if (refVideoUrls.length > 0) {
          // 示例视频（URL）- 支持多个
          result.videoReferenceAssets = refVideoUrls.map((url, index) => ({
            id: `example_${Date.now()}_${index}`,
            url: url,
          }));
          // 保持向后兼容：第一个视频赋值给 videoReferenceAsset
          result.videoReferenceAsset = {
            id: result.videoReferenceAssets[0].id,
            url: result.videoReferenceAssets[0].url,
          };
          console.log('[applyVideoPayloadWithAssets] 设置多个视频引用（URL）:', result.videoReferenceAssets);
        } else if (refVideoIds.length > 0) {
          // 资产库视频（ID）- 支持多个
          console.log('[applyVideoPayloadWithAssets] 开始获取多个视频引用资产, refVideoIds:', refVideoIds);
          try {
            const res = await assetService.getAssetList({ asset_id_list: refVideoIds });
            console.log('[applyVideoPayloadWithAssets] 获取视频引用资产响应:', res);
            if (res.data?.list && res.data.list.length > 0) {
              // 按照 refVideoIds 的顺序排序确保视频顺序与生成时一致
              const assetMap = new Map(res.data.list.map(asset => [asset.asset_id, asset.url]));
              // 同参考图：保序占位，避免 @视频N 下标错位
              result.videoReferenceAssets = refVideoIds.map(id => ({
                id: id,
                url: assetMap.get(id) || '',
              }));
              // 保持向后兼容：第一个视频赋值给 videoReferenceAsset
              if (result.videoReferenceAssets.length > 0) {
                result.videoReferenceAsset = {
                  id: result.videoReferenceAssets[0].id,
                  url: result.videoReferenceAssets[0].url,
                };
              }
              console.log('[applyVideoPayloadWithAssets] 设置多个视频引用（ID）:', result.videoReferenceAssets);
            } else {
              console.log('[applyVideoPayloadWithAssets] 资产列表为空:', res.data);
            }
          } catch (error) {
            console.error('[applyVideoPayloadWithAssets] 获取视频引用资产失败:', error);
          }
        } else {
          console.log('[applyVideoPayloadWithAssets] 没有视频引用');
        }

        // 处理参考图
        if (refImageIds.length > 0) {
          console.log('[applyVideoPayloadWithAssets] 开始获取参考图资产, refImageIds:', refImageIds);
          try {
            const res = await assetService.getAssetList({ asset_id_list: refImageIds });
            console.log('[applyVideoPayloadWithAssets] 获取参考图资产响应:', res);
            if (res.data?.list) {
              // 按照 refImageIds 的顺序排序，确保参考图顺序与生成时一致
              const assetMap = new Map(res.data.list.map(asset => [asset.asset_id, asset.url]));
              // 修复：@图片N ↔ ref_images[N-1] 要求回填列表与 payload.ref_images 同序同长。
              // 不得 filter 剔除拉取失败的资产（否则后续所有 @图片N 整体错位），
              // 改为按原顺序保留占位（url 置空），缺失项由 UI 层显示加载失败态。
              result.referenceAssets = refImageIds.map(id => ({
                id: id,
                url: assetMap.get(id) || '',
              }));
              console.log('[applyVideoPayloadWithAssets] 设置参考图:', result.referenceAssets);
            } else {
              console.log('[applyVideoPayloadWithAssets] 参考图资产列表为空');
            }
          } catch (error) {
            console.error('[applyVideoPayloadWithAssets] 获取参考图资产失败:', error);
          }
        } else {
          console.log('[applyVideoPayloadWithAssets] 没有参考图');
        }

        // 处理音频引用
        const refAudioIds = payload.ref_audios || [];
        if (refAudioIds.length > 0) {
          console.log('[applyVideoPayloadWithAssets] 开始获取音频引用资产, refAudioIds:', refAudioIds);
          try {
            const res = await assetService.getAssetList({ asset_id_list: refAudioIds });
            console.log('[applyVideoPayloadWithAssets] 获取音频引用资产响应:', res);
            if (res.data?.list && res.data.list.length > 0) {
              const assetMap = new Map(res.data.list.map(asset => [asset.asset_id, asset.url]));
              // 同参考图：保序占位，避免 @音频N 下标错位
              result.audioReferenceAssets = refAudioIds.map(id => ({
                id: id,
                url: assetMap.get(id) || '',
              }));
              console.log('[applyVideoPayloadWithAssets] 设置音频引用:', result.audioReferenceAssets);
            } else {
              console.log('[applyVideoPayloadWithAssets] 音频引用资产列表为空');
            }
          } catch (error) {
            console.error('[applyVideoPayloadWithAssets] 获取音频引用资产失败:', error);
          }
        } else {
          console.log('[applyVideoPayloadWithAssets] 没有音频引用');
        }

        // 处理文档引用（Wan3.0 多模态参考：media 数组，asset_type=Link 直接取 url，asset_type=File 查询资产）
        const mediaItems = payload.media || [];
        if (mediaItems.length > 0) {
          const linkItems = mediaItems.filter(m => m.asset_type === 'Link' && m.url);
          const fileAssetIds = mediaItems
            .filter(m => m.asset_type === 'File' && m.asset_id)
            .map(m => m.asset_id as string);
          result.docReferenceAssets = linkItems.map(m => ({
            id: `link:${m.url}`,
            url: m.url as string,
            name: m.url,
          }));
          if (fileAssetIds.length > 0) {
            console.log('[applyVideoPayloadWithAssets] 开始获取文档引用资产, fileAssetIds:', fileAssetIds);
            try {
              const res = await assetService.getAssetList({ asset_id_list: fileAssetIds });
              console.log('[applyVideoPayloadWithAssets] 获取文档引用资产响应:', res);
              if (res.data?.list && res.data.list.length > 0) {
                const assetMap = new Map(res.data.list.map(asset => [asset.asset_id, { url: asset.url, name: asset.name }]));
                result.docReferenceAssets.push(
                  ...fileAssetIds
                    .filter(id => assetMap.has(id))
                    .map(id => {
                      const asset = assetMap.get(id)!;
                      return { id, url: asset.url, name: asset.name };
                    })
                );
                console.log('[applyVideoPayloadWithAssets] 设置文档引用:', result.docReferenceAssets);
              } else {
                console.log('[applyVideoPayloadWithAssets] 文档引用资产列表为空');
              }
            } catch (error) {
              console.error('[applyVideoPayloadWithAssets] 获取文档引用资产失败:', error);
            }
          }
        } else {
          console.log('[applyVideoPayloadWithAssets] 没有文档引用');
        }
      
        // 首尾帧模式：即使只有首帧也需要回显首帧
        const idsToFetch: string[] = [];
        if (refImageIds.length > 0) idsToFetch.push(refImageIds[0]);
        if (tailImageId) idsToFetch.push(tailImageId);

        if (idsToFetch.length > 0) {
          const res = await assetService.getAssetList({ asset_id_list: idsToFetch });
          if (res.data?.list) {
            const assetMap = new Map(res.data.list.map(asset => [asset.asset_id, asset.url]));
            if (refImageIds.length > 0 && assetMap.has(refImageIds[0])) {
              result.firstFrameAsset = { id: refImageIds[0], url: assetMap.get(refImageIds[0])! };
            }
            if (tailImageId && assetMap.has(tailImageId)) {
              result.lastFrameAsset = { id: tailImageId, url: assetMap.get(tailImageId)! };
            }
          }
        }
      } else if (refImageIds.length > 0) {
        // 参考图模式 / 首尾帧模式（mode_type=2）
        const isKeyframeMode = payload.mode_type === 2;

        // 构建需要获取的资产 ID 列表（首帧 + 尾帧 + 参考图）
        const allIds: string[] = [...refImageIds];
        if (tailImageId) allIds.push(tailImageId);

        const res = await assetService.getAssetList({ asset_id_list: allIds });
        if (res.data?.list) {
          const assetMap = new Map(res.data.list.map(asset => [asset.asset_id, asset.url]));

          if (isKeyframeMode) {
            // 首帧：refImageIds[0]
            if (refImageIds.length > 0 && assetMap.has(refImageIds[0])) {
              result.firstFrameAsset = { id: refImageIds[0], url: assetMap.get(refImageIds[0])! };
            }
            // 尾帧：tailImageId
            if (tailImageId && assetMap.has(tailImageId)) {
              result.lastFrameAsset = { id: tailImageId, url: assetMap.get(tailImageId)! };
            }
            // 剩余参考图（从第2张开始）
            const remainingIds = refImageIds.slice(1);
            result.referenceAssets = remainingIds.map(id => ({ id, url: assetMap.get(id) || '' }));
          } else {
            // 纯粹参考图模式
            // 修复：保序占位，@图片N ↔ ref_images[N-1] 方能对齐
            result.referenceAssets = refImageIds.map(id => ({ id, url: assetMap.get(id) || '' }));
          }
        }
      }
      // 兜底：若 mode_type 非 3（历史/异常数据）但 payload 带 media，仍解析文档，避免编辑回显丢失文档
      if ((result.docReferenceAssets?.length ?? 0) === 0 && (payload.media?.length ?? 0) > 0) {
        const mediaItems = payload.media || [];
        const linkItems = mediaItems.filter(m => m.asset_type === 'Link' && m.url);
        const fileAssetIds = mediaItems
          .filter(m => m.asset_type === 'File' && m.asset_id)
          .map(m => m.asset_id as string);
        result.docReferenceAssets = linkItems.map(m => ({
          id: `link:${m.url}`,
          url: m.url as string,
          name: m.url,
        }));
        if (fileAssetIds.length > 0) {
          try {
            const res = await assetService.getAssetList({ asset_id_list: fileAssetIds });
            if (res.data?.list && res.data.list.length > 0) {
              const assetMap = new Map(res.data.list.map(asset => [asset.asset_id, { url: asset.url, name: asset.name }]));
              result.docReferenceAssets.push(
                ...fileAssetIds
                  .filter(id => assetMap.has(id))
                  .map(id => {
                    const asset = assetMap.get(id)!;
                    return { id, url: asset.url, name: asset.name };
                  })
              );
              console.log('[applyVideoPayloadWithAssets] 兜底设置文档引用:', result.docReferenceAssets);
            }
          } catch (error) {
            console.error('[applyVideoPayloadWithAssets] 兜底获取文档引用资产失败:', error);
          }
        }
      }

      console.log(result, 'result')
      return result;
    } catch (error: any) {
      console.error('应用视频参数时出错:', error);
      return rejectWithValue(error.message);
    }
  }
);

const videoGenerationSlice = createSlice({
  name: 'videoGeneration',
  initialState,
  reducers: {
    // Form actions
    setPrompt: (state, action: PayloadAction<string>) => {
      state.prompt = action.payload;
    },
    setModelPrompt: (state, action: PayloadAction<{ modelId: string; prompt: string }>) => {
      if (!state.modelPrompts) state.modelPrompts = {};
      state.modelPrompts[action.payload.modelId] = action.payload.prompt;
    },
    setModelMultimodalData: (state, action: PayloadAction<{ modelId: string; data: ModelMultimodalData }>) => {
      if (!state.modelMultimodalData) state.modelMultimodalData = {};
      state.modelMultimodalData[action.payload.modelId] = action.payload.data;
    },
    /**
     * 单选模型：整体替换当前选中项（数组长度恒为 1，保持与后端 model_id_list 结构一致）
     * 切换时按模型保存/恢复多模态素材，避免用户切模型丢参考素材
     */
    setSelectedModel: (state, action: PayloadAction<string>) => {
      const modelId = action.payload;
      const prevModelId = state.selectedModels[0];
      if (prevModelId === modelId) return;

      const isNewModelMultimodal = supportsMultiModalReference(modelId);
      const isPrevModelMultimodal = !!prevModelId && supportsMultiModalReference(prevModelId);

      // 1) 保存上一个多模态模型的数据
      if (prevModelId && isPrevModelMultimodal) {
        state.modelMultimodalData[prevModelId] = {
          uploadedImages: [...state.uploadedImages],
          videoReferences: [...state.videoReferences],
          audioReference: state.audioReference,
          audioReferences: [...(state.audioReferences || [])],
          docReferences: [...(state.docReferences || [])],
          referenceMode: state.referenceMode,
          firstFrame: state.firstFrame,
          lastFrame: state.lastFrame,
        };
      }

      // 2) 从多模态模型的'首尾帧'模式切到普通模型时，同步 referenceTab 为 '首尾帧'
      // 避免 firstFrame/lastFrame 图片已存在但 referenceTab 仍为 '参考图' 导致校验失败
      if (
        isPrevModelMultimodal &&
        !isNewModelMultimodal &&
        state.referenceMode === '首尾帧' &&
        (state.firstFrame || state.lastFrame) &&
        state.referenceTab !== '首尾帧'
      ) {
        state.referenceTab = '首尾帧';
        // 同步更新 imageHistory，确保 keyframes 与当前 firstFrame/lastFrame 一致
        if (!state.imageHistory) {
          state.imageHistory = { referenceImages: [], keyframes: { firstFrame: null, lastFrame: null } };
        }
        if (!state.imageHistory.keyframes) {
          state.imageHistory.keyframes = { firstFrame: null, lastFrame: null };
        }
        state.imageHistory.keyframes.firstFrame = state.firstFrame;
        state.imageHistory.keyframes.lastFrame = state.lastFrame;
      }

      // 3) 切到多模态模型时恢复其已保存的素材
      if (isNewModelMultimodal) {
        const savedData = state.modelMultimodalData[modelId];
        if (savedData) {
          state.uploadedImages = [...savedData.uploadedImages];
          state.videoReferences = [...savedData.videoReferences];
          state.audioReference = savedData.audioReference;
          state.audioReferences = [...(savedData.audioReferences || [])];
          state.docReferences = [...(savedData.docReferences || [])];
          // 恢复首尾帧数据
          state.firstFrame = savedData.firstFrame ?? null;
          state.lastFrame = savedData.lastFrame ?? null;
          state.referenceMode = (savedData.referenceMode ?? '多模态参考') as '首尾帧' | '多模态参考';
        }
      }

      state.selectedModels = [modelId];
    },
    clearRestoringFromEdit: (state) => {
      state.isRestoringFromEdit = false;
    },
    setDuration: (state, action: PayloadAction<string>) => {
      state.duration = action.payload;
    },
    setAspectRatio: (state, action: PayloadAction<string>) => {
      console.log(action.payload, 'aspectRatioVideo')
      state.aspectRatioVideo = action.payload;
    },
    setResolutionVideo: (state, action: PayloadAction<string>) => {
      console.log(action.payload, 'resolutionVideo')
      state.resolutionVideo = action.payload;
    },
    setResolution: (state, action: PayloadAction<string>) => {
      state.resolution = action.payload;
    },
    setGenerationCount: (state, action: PayloadAction<number>) => {
      state.generationCount = action.payload;
    },
    setReferenceTab: (state, action: PayloadAction<'参考图' | '首尾帧'>) => {
      const newTab = action.payload;
      const oldTab = state.referenceTab;

      state.referenceTab = newTab;

      // 根据新的模式调整模型选择
      // if (state.models.length > 0) {
      //   const kling16Id = SLICE_MODEL_CONFIG.KLING_16_ID; // 可灵 1.6
      //   const jimeng3Id = SLICE_MODEL_CONFIG.JIMENG_3_ID; // 即梦 3.0
      //   const hailuo02Id = SLICE_MODEL_CONFIG.HAILUO_02_ID; // 海螺02

      //   if (newTab === '首尾帧') {
      //     // 切换到首尾帧模式：确保选择可灵1.6和即梦3.0
      //     const hasKling16 = state.models.some(m => m.model_id === kling16Id);
      //     const hasJimeng3 = state.models.some(m => m.model_id === jimeng3Id);

      //     // const newSelectedModels: string[] = [];
      //     const newSelectedModels = [...state.selectedModels];

      //     const hasHailuo02Id = newSelectedModels.includes(hailuo02Id);

      //     if (newSelectedModels.length === 1 && hasHailuo02Id) {
      //       if (hasKling16 && !newSelectedModels.includes(kling16Id)) {
      //         newSelectedModels.push(kling16Id);
      //       }
      //     } else {
      //       if (hasKling16 && !newSelectedModels.includes(kling16Id)) {
      //         newSelectedModels.push(kling16Id);
      //       }
      //       if (hasJimeng3 && !newSelectedModels.includes(jimeng3Id)) {
      //         newSelectedModels.push(jimeng3Id);
      //       }
      //     }
      //     state.selectedModels = newSelectedModels;
      //   } else {
      //     // 切换到参考图模式：保持当前选择的模型，不做任何自动调整
      //     // 在参考图模式下，所有模型都可以使用，让用户自由选择
      //     // 如果当前没有选中任何模型，则默认选择可灵1.6
      //     if (state.selectedModels.length === 0) {
      //       const hasKling16 = state.models.some(m => m.model_id === kling16Id);
      //       if (hasKling16) {
      //         state.selectedModels = [kling16Id];
      //       } else if (state.models.length > 0) {
      //         state.selectedModels = [state.models[0].model_id];
      //       }
      //     }
      //     // 如果已有选中的模型，则保持不变
      //   }
      // }

      // 图片历史保存和恢复逻辑
      console.log(`[VideoGeneration] 标签切换: ${oldTab} -> ${newTab}`);

      // 确保 imageHistory 存在，如果不存在则初始化
      if (!state.imageHistory) {
        state.imageHistory = {
          referenceImages: [],
          keyframes: {
            firstFrame: null,
            lastFrame: null,
          },
        };
        console.log('[VideoGeneration] 初始化图片历史记录');
      }

      // 确保 keyframes 存在
      if (!state.imageHistory.keyframes) {
        state.imageHistory.keyframes = {
          firstFrame: null,
          lastFrame: null,
        };
      }

      // 确保 referenceImages 存在
      if (!state.imageHistory.referenceImages) {
        state.imageHistory.referenceImages = [];
      }

      // 1. 保存当前模式的图片到历史记录
      if (oldTab === '参考图') {
        // 保存参考图模式的图片到历史
        state.imageHistory.referenceImages = [...state.uploadedImages];
        // 同步当前的首帧到首尾帧历史，便于切换到首尾帧时可见
        if (state.firstFrame) {
          state.imageHistory.keyframes.firstFrame = state.firstFrame;
        }
        console.log('[VideoGeneration] 保存参考图历史:', state.imageHistory.referenceImages.length);
      } else if (oldTab === '首尾帧') {
        // 保存首尾帧模式的图片到历史
        state.imageHistory.keyframes.firstFrame = state.firstFrame;
        state.imageHistory.keyframes.lastFrame = state.lastFrame;
        console.log('[VideoGeneration] 保存首尾帧历史:', {
          firstFrame: state.imageHistory.keyframes.firstFrame ? 'exists' : 'null',
          lastFrame: state.imageHistory.keyframes.lastFrame ? 'exists' : 'null',
        });
      }

      // 2. 恢复新模式的图片历史
      if (newTab === '参考图') {
        // 恢复参考图模式的历史
        state.uploadedImages = [...state.imageHistory.referenceImages];
        console.log('[VideoGeneration] 恢复参考图历史:', state.uploadedImages.length);
      } else if (newTab === '首尾帧') {
        // 恢复首尾帧模式的历史
        state.firstFrame = state.imageHistory.keyframes.firstFrame;
        state.lastFrame = state.imageHistory.keyframes.lastFrame;
        console.log('[VideoGeneration] 恢复首尾帧历史:', {
          firstFrame: state.firstFrame ? 'exists' : 'null',
          lastFrame: state.lastFrame ? 'exists' : 'null',
        });
      }
    },

    // Image management actions
    setUploadedImages: (state, action: PayloadAction<UploadedImage[]>) => {
      state.uploadedImages = action.payload;
    },
    addUploadedImage: (state, action: PayloadAction<UploadedImage>) => {
      // 图生视频的参考图只允许一张，新上传的图片替换现有的参考图
      state.uploadedImages = [action.payload];
    },
    removeUploadedImage: (state, action: PayloadAction<number | string>) => {
      state.uploadedImages = state.uploadedImages.filter(img => img.id !== action.payload);
    },
    selectUploadedImage: (state, action: PayloadAction<number | string>) => {
      state.uploadedImages = state.uploadedImages.map(img => ({
        ...img,
        isSelected: img.id === action.payload,
      }));
    },
    setFirstFrame: (state, action: PayloadAction<FrameImage | null>) => {
      state.firstFrame = action.payload;
      if (action.payload === null && state.imageHistory?.keyframes) {
        state.imageHistory.keyframes.firstFrame = null;
      }
    },
    setLastFrame: (state, action: PayloadAction<FrameImage | null>) => {
      state.lastFrame = action.payload;
      if (action.payload === null && state.imageHistory?.keyframes) {
        state.imageHistory.keyframes.lastFrame = null;
      }
    },
    setVideoReference: (state, action: PayloadAction<FrameImage | null>) => {
      state.videoReference = action.payload;
      state.videoReferences = action.payload ? [action.payload] : [];
    },
    setVideoReferences: (state, action: PayloadAction<FrameImage[]>) => {
      state.videoReferences = action.payload;
      state.videoReference = action.payload[0] ?? null;
    },
    setAudioReference: (state, action: PayloadAction<FrameImage | null>) => {
      state.audioReference = action.payload;
      state.audioReferences = action.payload ? [action.payload] : [];
    },
    setAudioReferences: (state, action: PayloadAction<FrameImage[]>) => {
      state.audioReferences = action.payload;
      state.audioReference = action.payload[0] ?? null;
    },
    setDocReferences: (state, action: PayloadAction<FrameImage[]>) => {
      // 多模态参考模式的文档引用（Wan3.0），无单引用历史包袱，仅维护数组
      state.docReferences = action.payload;
    },
    setReferenceMode: (state, action: PayloadAction<'首尾帧' | '多模态参考'>) => {
      state.referenceMode = action.payload;
    },
    swapFrames: state => {
      const temp = state.firstFrame;
      state.firstFrame = state.lastFrame;
      state.lastFrame = temp;
    },
    setRecommendImages: (state, action: PayloadAction<FrameImage[]>) => {
      state.recommendImages = action.payload;
    },
    shuffleRecommendImages: state => {
      // 重新从所有示例中随机选择
      const shuffled = [...IMAGE_TO_VIDEO_EXAMPLES].sort(() => 0.5 - Math.random());
      state.recommendImages = shuffled.slice(0, 5).map((example, index) => ({
        id: `recommend_${Date.now()}_${index}`,
        src: example.coverPageURL,
      }));
    },

    // 真人视频开关（仅 Seedance 2.0）
    setRealVideo: (state, action: PayloadAction<boolean>) => {
      state.real_video = action.payload;
    },

    // UI actions
    setSwapHover: (state, action: PayloadAction<boolean>) => {
      state.swapHover = action.payload;
    },
    setPreviewVisible: (state, action: PayloadAction<boolean>) => {
      state.preview.visible = action.payload;
    },
    setPreviewFrame: (state, action: PayloadAction<'first' | 'last' | null>) => {
      state.preview.frame = action.payload;
    },
    showPreview: (state, action: PayloadAction<'first' | 'last'>) => {
      state.preview.visible = true;
      state.preview.frame = action.payload;
    },
    hidePreview: state => {
      state.preview.visible = false;
      state.preview.frame = null;
    },

    // Data actions
    setModels: (state, action: PayloadAction<ModelInfo[]>) => {
      state.models = action.payload;
      // 历史持久化数据可能是多模型（旧版多选），单选模式下收敛为第一个
      if (state.selectedModels.length > 1) {
        state.selectedModels = [state.selectedModels[0]];
      }
      // 如果没有选中的模型且有可用模型，设置默认模型（可灵 1.6，找不到则第一个可用模型）
      if (state.selectedModels.length === 0 && action.payload.length > 0) {
        const kling16Id = SLICE_MODEL_CONFIG.KLING_16_ID; // 可灵 1.6
        const defaultModel = action.payload.find(model => model.model_id === kling16Id);
        state.selectedModels = [defaultModel ? defaultModel.model_id : action.payload[0].model_id];
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    // 手动更新图片历史记录（用于图生视频功能）
    updateImageHistory: (
      state,
      action: PayloadAction<{
        referenceImages?: UploadedImage[];
        keyframes?: {
          firstFrame: FrameImage | null;
          lastFrame: FrameImage | null;
        };
      }>
    ) => {
      if (!state.imageHistory) {
        state.imageHistory = {
          referenceImages: [],
          keyframes: {
            firstFrame: null,
            lastFrame: null,
          },
        };
      }
      if (action.payload.referenceImages) {
        state.imageHistory.referenceImages = [...action.payload.referenceImages];
      }
      if (action.payload.keyframes) {
        if (!state.imageHistory.keyframes) {
          state.imageHistory.keyframes = {
            firstFrame: null,
            lastFrame: null,
          };
        }
        state.imageHistory.keyframes.firstFrame = action.payload.keyframes.firstFrame;
        state.imageHistory.keyframes.lastFrame = action.payload.keyframes.lastFrame;
      }
      console.log('[VideoGeneration] 手动更新图片历史记录:', action.payload);
    },

    // Reset actions
    resetForm: state => {
      state.prompt = '';
      state.selectedModels = state.models.length > 0 ? [state.models[0].model_id] : [];
      state.duration = '5 s';
      state.resolution = '自动匹配';
      state.generationCount = 1;
      state.uploadedImages = [];
      state.firstFrame = null;
      state.lastFrame = null;
      state.error = null;
    },
    resetImages: state => {
      state.uploadedImages = [];
      state.firstFrame = null;
      state.lastFrame = null;
    },
    resetImageHistory: state => {
      // 确保 imageHistory 存在
      if (!state.imageHistory) {
        state.imageHistory = {
          referenceImages: [],
          keyframes: {
            firstFrame: null,
            lastFrame: null,
          },
        };
      } else {
        state.imageHistory.referenceImages = [];
        if (!state.imageHistory.keyframes) {
          state.imageHistory.keyframes = {
            firstFrame: null,
            lastFrame: null,
          };
        } else {
          state.imageHistory.keyframes.firstFrame = null;
          state.imageHistory.keyframes.lastFrame = null;
        }
      }
    },
    resetCurrentModeImages: state => {
      if (state.referenceTab === '参考图') {
        state.uploadedImages = [];
      } else {
        state.firstFrame = null;
        state.lastFrame = null;
      }
    },
    resetToInitialState: () => initialState,
  },
  extraReducers: builder => {
    builder
      // 处理 Redux persist 的 rehydrate：旧版持久化的 referenceMode 为首尾帧，
      // 统一迁移为新的默认值"多模态参考"。
      // .addCase(REHYDRATE, (state, action: any) => {
      //   if (action.payload?.videoGeneration) {
      //     const persisted = action.payload.videoGeneration;
      //     if (persisted.referenceMode === '首尾帧') {
      //       console.log('[REHYDRATE] 检测到旧版首尾帧持久化数据，迁移为多模态参考');
      //       state.referenceMode = '多模态参考';
      //     }
      //   }
      // })
      .addCase(applyVideoPayloadWithAssets.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(applyVideoPayloadWithAssets.fulfilled, (state, action) => {
        state.isLoading = false;
        // 标记为编辑回显状态，防止 VideoSiderContentNew 的 selectedModels useEffect
        // 将"从多模态模型切换到非多模态模型"误判为用户主动切换而清空 uploadedImages
        state.isRestoringFromEdit = true;
        const { payload, firstFrameAsset, lastFrameAsset, referenceAssets, videoReferenceAsset, videoReferenceAssets, audioReferenceAssets, docReferenceAssets } = action.payload;

        console.log('[applyVideoPayloadWithAssets.fulfilled] 开始处理，当前 referenceMode:', state.referenceMode);
        console.log('[applyVideoPayloadWithAssets.fulfilled] payload.mode_type:', payload.mode_type);
        console.log('[applyVideoPayloadWithAssets.fulfilled] 处理结果:', {
          mode_type: payload.mode_type,
          firstFrameAsset,
          lastFrameAsset,
          referenceAssets: referenceAssets?.length,
          videoReferenceAsset,
          videoReferenceAssets: videoReferenceAssets?.length,
        });

        // 防御性检查：确保payload存在
        if (!payload) {
          console.error('applyVideoPayloadWithAssets: payload为空');
          return;
        }

        try {
          // 多模态模式：用 meta_list 反向重建含 @mention 的 prompt；否则直接取 prompt 字段
          let reconstructedPrompt: string;
          if (payload.meta_list && payload.meta_list.length > 0) {
            reconstructedPrompt = reconstructPromptFromMetaList(
              payload.meta_list,
              payload.ref_images || [],
              payload.ref_videos || [],
              payload.ref_audios || [],
            );
          } else {
            reconstructedPrompt = payload.prompt || '';
          }
          state.prompt = reconstructedPrompt;

          // 同步写入 modelPrompts，防止 selectedModels 变化时的 @mention 清除逻辑覆盖重建结果
          if (payload.model_id_list?.length === 1) {
            state.modelPrompts[payload.model_id_list[0]] = reconstructedPrompt;
          }

          // 验证并设置时长
          if (payload.duration && payload.duration > 0) {
            state.duration = `${payload.duration} s`;
          }

          // 验证并设置生成数量
          if (payload.number && payload.number > 0) {
            state.generationCount = payload.number;
          }


          // 验证并设置声音参数
          if (payload.sound !== undefined) {
            state.sound = payload.sound;
          }

          // 验证并设置真人视频参数
          if (payload.real_video !== undefined) {
            state.real_video = payload.real_video;
            console.log('[applyVideoPayloadWithAssets.fulfilled] 设置真人视频:', payload.real_video);
          }

          // 应用模型选择（只更新 selectedModels，不提前保存 modelMultimodalData）
          // modelMultimodalData 将在所有图片/视频/音频设置完毕后统一保存，避免保存旧数据
          // 单选模式：历史任务可能包含多个模型，取第一个
          if (payload.model_id_list && Array.isArray(payload.model_id_list) && payload.model_id_list.length > 0) {
            state.selectedModels = [payload.model_id_list[0]];
          }

          // 应用图片资产
          // 判断是否为可灵 3.0 Omni 模型
          const klingV3OmniId = '01KFW2E2SMK5PFFF5RMK5EBXSW';
          const isKlingV3Omni = payload.model_id_list?.includes(klingV3OmniId);
          if (payload.mode_type === 3) {
            // 多模态参考模式
            state.referenceMode = '多模态参考';
            state.referenceTab = '参考图';

            // 设置多个视频引用（支持 Seedance 2.0 多视频模式）
            if (videoReferenceAssets && videoReferenceAssets.length > 0) {
              state.videoReferences = videoReferenceAssets.map(asset => ({
                id: asset.id,
                src: asset.url,
              }));
              // 保持向后兼容：第一个视频赋值给 videoReference
              state.videoReference = {
                id: videoReferenceAssets[0].id,
                src: videoReferenceAssets[0].url,
              };
              console.log('[applyVideoPayloadWithAssets.fulfilled] 设置多个视频引用:', state.videoReferences.length, '个');
            } else if (videoReferenceAsset) {
              // 向后兼容：单个视频引用
              state.videoReference = {
                id: videoReferenceAsset.id,
                src: videoReferenceAsset.url,
              };
              state.videoReferences = [state.videoReference];
              console.log('[applyVideoPayloadWithAssets.fulfilled] 设置单个视频引用:', state.videoReference);
            } else {
              // 保留已有 videoReferences（可能从 localStorage 恢复），避免刷新时覆盖
              if (state.videoReferences.length === 0) {
                state.videoReference = null;
              }
              console.log('[applyVideoPayloadWithAssets.fulfilled] 没有视频引用，保留已有状态');
            }

            // 设置参考图
            if (referenceAssets && referenceAssets.length > 0) {
              state.uploadedImages = referenceAssets.map((asset) => ({
                id: asset.id,
                src: asset.url,
                isSelected: true,
              }));
              console.log('[applyVideoPayloadWithAssets.fulfilled] 设置参考图:', state.uploadedImages.length, '张');
            } else {
              // 保留已有 uploadedImages（可能从 localStorage 恢复），避免刷新时覆盖
              console.log('[applyVideoPayloadWithAssets.fulfilled] 没有参考图，保留已有状态');
            }

            // 设置音频引用 - 只有在多模态参考模式下才处理音频
            if (payload.mode_type === 3 && audioReferenceAssets && audioReferenceAssets.length > 0) {
              state.audioReference = {
                id: audioReferenceAssets[0].id,
                src: audioReferenceAssets[0].url,
              };
              state.audioReferences = audioReferenceAssets.map(asset => ({
                id: asset.id,
                src: asset.url,
              }));
              console.log('[applyVideoPayloadWithAssets.fulfilled] 设置音频引用:', audioReferenceAssets.length, '个');
            } else {
              // 保留已有 audioReferences（可能从 localStorage 恢复），避免刷新时覆盖
              if (state.audioReferences.length === 0) {
                state.audioReference = null;
              }
              console.log('[applyVideoPayloadWithAssets.fulfilled] 没有音频引用，保留已有状态');
            }

            // 设置文档引用 - 多模态参考模式下处理文档（Wan3.0）；
            // 兼容 payload.mode_type 异常但带 media 的历史数据，只要有解析出的文档就回显
            if (docReferenceAssets && docReferenceAssets.length > 0 && (payload.mode_type === 3 || (payload.media?.length ?? 0) > 0)) {
              state.docReferences = docReferenceAssets.map(asset => ({
                id: asset.id,
                src: asset.url,
                name: asset.name,
              }));
              console.log('[applyVideoPayloadWithAssets.fulfilled] 设置文档引用:', docReferenceAssets.length, '个');
            } else {
              // 保留已有 docReferences（可能从 localStorage 恢复），避免刷新时覆盖
              if ((state.docReferences || []).length === 0) {
                state.docReferences = [];
              }
              console.log('[applyVideoPayloadWithAssets.fulfilled] 没有文档引用，保留已有状态');
            }

            // 清空首尾帧
            state.firstFrame = null;
            state.lastFrame = null;
            // 同步清空 imageHistory.keyframes，防止 redux-persist 持久化旧数据
            // 刷新后 setReferenceTab('首尾帧') 会从 imageHistory.keyframes 恢复旧首尾帧
            if (state.imageHistory?.keyframes) {
              state.imageHistory.keyframes.firstFrame = null;
              state.imageHistory.keyframes.lastFrame = null;
            }
          } else if (payload.mode_type === 2 || (lastFrameAsset && firstFrameAsset)) {
            // 优先使用 mode_type 判断；兼容老数据用字段存在性推断
            state.referenceTab = '首尾帧';
            // 如果是可灵3 Omni 模型，设置首尾帧模式
            if (isKlingV3Omni) {
              state.referenceMode = '首尾帧';
            }
            if (firstFrameAsset) {
              state.firstFrame = {
                id: firstFrameAsset.id,
                src: firstFrameAsset.url,
              };
            } else {
              state.firstFrame = null;
            }
            if (lastFrameAsset) {
              state.lastFrame = {
                id: lastFrameAsset.id,
                src: lastFrameAsset.url,
              };
            } else {
              state.lastFrame = null;
            }
            
            // 设置音频引用 - 只有在多模态参考模式下才处理音频
            if (payload.mode_type === 3 && audioReferenceAssets && audioReferenceAssets.length > 0) {
              state.audioReference = {
                id: audioReferenceAssets[0].id,
                src: audioReferenceAssets[0].url,
              };
              state.audioReferences = audioReferenceAssets.map(asset => ({
                id: asset.id,
                src: asset.url,
              }));
              console.log('[applyVideoPayloadWithAssets.fulfilled] 设置音频引用:', audioReferenceAssets.length, '个');
            } else {
              state.audioReference = null;
              state.audioReferences = [];
              console.log('[applyVideoPayloadWithAssets.fulfilled] 没有音频引用');
            }
            state.docReferences = [];
            
            state.uploadedImages = [];
            state.videoReference = null;
          } else if (payload.mode_type === 1 || (referenceAssets && referenceAssets.length > 0)) {
            // 优先使用 mode_type 判断；兼容老数据用字段存在性推断
            state.referenceTab = '参考图';
            // 如果是可灵3 Omni模型，判断是否有视频引用来设置参考模式
            if (isKlingV3Omni) {
              if (videoReferenceAsset) {
                state.referenceMode = '多模态参考';
                console.log('[applyVideoPayloadWithAssets.fulfilled] 可灵3 Omni多模态参考模式（有视频引用）');
              } else {
                state.referenceMode = '首尾帧';
                console.log('[applyVideoPayloadWithAssets.fulfilled] 可灵3 Omni首尾帧模式（无视频引用）');
              }
            }
            // 所有参考图都应该被选中计算米粒值
            state.uploadedImages = referenceAssets.map((asset) => ({
              id: asset.id,
              src: asset.url,
              isSelected: true,
            }));
            state.firstFrame = null;
            state.lastFrame = null;
            state.videoReference = null;
            
            // 设置音频引用 - 只有在多模态参考模式下才处理音频
            if (payload.mode_type === 3 && audioReferenceAssets && audioReferenceAssets.length > 0) {
              state.audioReference = {
                id: audioReferenceAssets[0].id,
                src: audioReferenceAssets[0].url,
              };
              state.audioReferences = audioReferenceAssets.map(asset => ({
                id: asset.id,
                src: asset.url,
              }));
              console.log('[applyVideoPayloadWithAssets.fulfilled] 设置音频引用:', audioReferenceAssets.length, '个');
            } else {
              state.audioReference = null;
              state.audioReferences = [];
              console.log('[applyVideoPayloadWithAssets.fulfilled] 没有音频引用');
            }
            state.docReferences = [];
          }

          // 所有图片/视频/音频/模式设置完毕后，统一保存到 modelMultimodalData
          // 必须在最后保存，否则 effect 恢复时读到旧数据会把刚设好的正确内容清掉
          payload.model_id_list?.forEach(modelId => {
            if (supportsMultiModalReference(modelId)) {
              state.modelMultimodalData[modelId] = {
                uploadedImages: state.uploadedImages,
                videoReferences: state.videoReferences || [],
                audioReference: state.audioReference,
                audioReferences: state.audioReferences || [],
                referenceMode: state.referenceMode,
                firstFrame: state.firstFrame,
                lastFrame: state.lastFrame,
              };
            }
          });
        } catch (error) {
          console.error('应用视频payload时发生错误:', error);
        }
      })
      .addCase(applyVideoPayloadWithAssets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPrompt,
  setModelPrompt,
  setModelMultimodalData,
  setSelectedModel,
  clearRestoringFromEdit,
  setDuration,
  setResolution,
  setGenerationCount,
  setReferenceTab,
  setUploadedImages,
  addUploadedImage,
  removeUploadedImage,
  selectUploadedImage,
  setFirstFrame,
  setLastFrame,
  setVideoReference,
  setVideoReferences,
  setAudioReference,
  setAudioReferences,
  setDocReferences,
  setReferenceMode,
  swapFrames,
  setRecommendImages,
  shuffleRecommendImages,
  setSwapHover,
  setPreviewVisible,
  setPreviewFrame,
  showPreview,
  hidePreview,
  setModels,
  setLoading,
  setError,
  resetForm,
  resetImages,
  resetImageHistory,
  resetCurrentModeImages,
  resetToInitialState: resetVideoToInitialState,
  updateImageHistory,
  setAspectRatio,
  setResolutionVideo,
  setRealVideo
} = videoGenerationSlice.actions;

export default videoGenerationSlice.reducer;
